import { useNavigate } from "react-router-dom";
import { useNavigationContext } from "@/context/navigation.context";

export const useCustomNavigation = () => {
  const { isBack, setIsBack } = useNavigationContext();
  const navigate = useNavigate();

  const navigateTo = (path: string, options?: any) => {
    setIsBack(false);
    navigate(path, {
      ...options,
      state: {
        ...options?.state,
        fromApp: true, // Mark navigation as coming from within the app
      },
    });
  };

  // Navigate to an explicit path while playing the BACK transition (the page
  // slides in from the left, like navigateBack) instead of the forward slide.
  // Use this for close/cancel/leave gestures whose destination is known and must
  // be deterministic — i.e. where navigateBack()'s history-based navigate(-1)
  // can't be used: the scan-flow close (in the no-scans share there is no in-app
  // entry behind /start-scan) and the New Avatar cancel (must land on home, not
  // step back into a scan step). Behaves like navigateTo (pushes an entry,
  // marks fromApp) but keeps the reverse animation direction.
  const navigateBackTo = (path: string, options?: any) => {
    setIsBack(true);
    navigate(path, {
      ...options,
      state: {
        ...options?.state,
        fromApp: true,
      },
    });
  };

  const navigateBack = () => {
    setIsBack(true);

    // Check if we can safely go back within the app
    const canGoBack = () => {
      // Check if history length indicates we can go back
      if (window.history.length <= 1) {
        return false;
      }

      // Go back if react-router has an in-app entry behind the current one.
      // react-router-dom v6 tracks its stack position in window.history.state.idx
      // and nests the user's navigation state (our `fromApp` flag) under `.usr`,
      // NOT at the top level — so idx > 0 (not `history.state.fromApp`) is the
      // reliable "there is a previous in-app entry" signal, and navigate(-1)
      // stays inside the app. (The old check read `history.state.fromApp` at the
      // top level, which is always undefined under RR v6, so navigateBack always
      // fell through to navigate("/") → home.)
      const currentState = window.history.state;
      if ((currentState?.idx ?? 0) > 0) {
        return true;
      }

      // idx === 0 (first app-managed entry — fresh load or a replace): only go
      // back if the referrer is same-origin; otherwise fall through to home.
      const referrer = document.referrer;
      if (referrer) {
        try {
          const referrerUrl = new URL(referrer);
          const currentUrl = new URL(window.location.href);
          // If referrer is from the same origin, we can go back
          if (referrerUrl.origin === currentUrl.origin) {
            return true;
          }
        } catch (e) {
          // Invalid URL, can't go back safely
          return false;
        }
      }

      return false;
    };

    if (canGoBack()) {
      navigate(-1);
    } else {
      // No valid history within the app, go to home page
      navigate("/");
    }
  };

  return { navigateTo, navigateBackTo, navigateBack, isBack };
};
